import global from 'global';
import { noop } from 'lodash';
import decode from 'jwt-decode';
import BaseProvider, {
  normalizeAccessObject,
  normalizeIdentity,
  onLogoutSymbol,
  onLoginSymbol,
} from './BaseProvider';

import Api from '../../Api';

const tulipAuthHeader = 'TulipAuth';

const symbols = {
  api: Symbol('Api instance property'),
  options: Symbol('Options object property'),
  tokens: Symbol('JWT tokens property'),
};

const localStorage = global.localStorage;
const tokenStoreKey = 'tulip-web-sdk-jwt-token';
const refreshTokenStoreKey = 'tulip-web-sdk-jwt-refresh-token';

const defaultOptions = {
  loginUrl: '/api/employees/login',
  renewUrl: '/api/employees/refresh_token',
  logoutUrl: '/api/employees/logout',

  storeToken(token) {
    if (!token) {
      localStorage.removeItem(tokenStoreKey);
    } else {
      localStorage.setItem(tokenStoreKey, token);
    }
  },

  readToken() {
    return localStorage.getItem(tokenStoreKey);
  },

  storeRefreshToken(token) {
    if (!token) {
      localStorage.removeItem(refreshTokenStoreKey);
    } else {
      localStorage.setItem(refreshTokenStoreKey, token);
    }
  },

  readRefreshToken() {
    return localStorage.getItem(refreshTokenStoreKey);
  },
};

export const createAccessObject = (idToken, refreshToken = null) => {
  if (!idToken) {
    return normalizeAccessObject({});
  }

  let decoded;

  try {
    decoded = decode(idToken);
  } catch (error) {
    return normalizeAccessObject({});
  }

  return normalizeAccessObject({
    authType: 'jwt',
    idToken,
    refreshToken,
    expiresAt: decoded.exp,
  });
};

export const createIdentity = (idToken) => {
  if (!idToken) {
    return null;
  }

  let decoded;

  try {
    decoded = decode(idToken);
  } catch (error) {
    return null;
  }

  return normalizeIdentity({
    id: decoded.employee_id,
    roles: decoded.roles || [],
    isAdmin: decoded.is_admin || false,
  });
};

export default class JWTProvider extends BaseProvider {
  constructor(options = {}, apiInstance = Api) {
    super();

    this[symbols.api] = apiInstance;
    this[symbols.tokens] = {};
    this[symbols.options] = {
      ...defaultOptions,
      options,
    };
  }

  async init(options) {
    await super.init(options);

    const { readToken = noop, readRefreshToken = noop } = this[symbols.options];

    this[symbols.tokens] = {
      token: await readToken(),
      refresh: await readRefreshToken(),
    };

    return this;
  }

  async login({ username, password }) {
    const {
      loginUrl,
      storeToken = noop,
      storeRefreshToken = noop,
    } = this[symbols.options];

    const { token, refresh_token: refresh } = await this[symbols.api].request(loginUrl, {
      method: 'POST',
      body: {
        username,
        password,
      },
    });

    this[symbols.tokens] = {
      token,
      refresh,
    };

    storeToken(token);
    storeRefreshToken(refresh);

    this[onLoginSymbol]();

    return this.getIdentity();
  }

  async logout() {
    const { logoutUrl } = this[symbols.options];

    await this[symbols.api].request(logoutUrl, {
      method: 'POST',
      body: {
        app_id: null,
        UUID: null,
      },
    });

    this.clear();
    this[onLogoutSymbol]();
  }

  async renew() {
    const { storeToken = noop } = this[symbols.options];
    const tokens = this[symbols.tokens];

    if (!tokens.refresh) {
      return this.getAccessObject();
    }

    const { renewUrl } = this[symbols.options];
    const { id_token: token } = await this[symbols.api].request(renewUrl, {
      method: 'POST',
      body: {
        refresh_token: tokens.refresh,
      },
    });

    this[symbols.tokens] = {
      ...tokens,
      token,
    };

    storeToken(token);

    return this.getAccessObject();
  }

  async clear() {
    const {
      storeToken = noop,
      storeRefreshToken = noop,
    } = this[symbols.options];

    this[symbols.tokens] = {};

    storeRefreshToken(null);
    storeToken(null);
  }

  getIdentity() {
    const { idToken } = this.getAccessObject();

    return createIdentity(idToken);
  }

  getAccessObject() {
    const { token = null, refresh = null } = this[symbols.tokens];

    return createAccessObject(token, refresh);
  }

  getRequestHeaders() {
    const { idToken } = this.getAccessObject();

    return idToken ? {
      [tulipAuthHeader]: idToken,
    } : {};
  }
}



// WEBPACK FOOTER //
// ./src/Auth/providers/JWTProvider.js